import React, { useCallback, useState } from "react";
import { MicrosoftLogo } from "../../ui-library/_icons/MicrosoftLogo.tsx";
import { Button } from "../../ui-library/Button/Button.tsx";
import { Card, CardTitle } from "../../ui-library/Card/Card.tsx";
import { LoadingDots } from "../../ui-library/LoadingDots/LoadingDots.tsx";
import styles from "./Login.module.css";
import { PasswordlessForm } from "./PasswordlessForm.tsx";
import { ConditionalLoginForm } from "./ConditionalLoginForm.tsx";
import { PasswordlessConfirmation } from "./PasswordlessConfirmation.tsx";

export const Login: React.FC<{
  onSignInWithMicrosoft?: () => Promise<void>;
  onSignInWithPasswordless?: (email: string) => Promise<boolean>;
}> = ({ onSignInWithMicrosoft, onSignInWithPasswordless }) => {
  const [loading, setLoading] = useState(false);
  const [submittedEmail, setSubmittedEmail] = useState<string | null>(null);
  const [failedEmail, setFailedEmail] = useState<string | null>(null);
  const microsoftLoginHandler = useCallback(async () => {
    if (!onSignInWithMicrosoft) return;
    setLoading(true);
    await onSignInWithMicrosoft();
    setLoading(false);
  }, [onSignInWithMicrosoft]);

  const passwordlessLoginHandler = useCallback(
    async (email: string) => {
      if (!onSignInWithPasswordless) return;
      if (email) {
        setLoading(true);
        try {
          const result = await onSignInWithPasswordless(email);
          if (!result) {
            setFailedEmail(email);
            return;
          }
          setSubmittedEmail(email);
        } finally {
          setLoading(false);
        }
      }
    },
    [onSignInWithPasswordless],
  );

  const handleTryDifferentEmail = useCallback(() => {
    setSubmittedEmail(null);
  }, []);

  const showOnlyMicrosoftLogin = !!(
    onSignInWithMicrosoft && !onSignInWithPasswordless
  );
  const showConditionalLogin = !!(
    onSignInWithPasswordless && onSignInWithMicrosoft
  );
  const showOnlyPasswordless = !!(
    onSignInWithPasswordless && !onSignInWithMicrosoft
  );

  return (
    <div className={styles.LoginWrapper}>
      <Card className={styles.LoginCard}>
        {submittedEmail ? (
          <PasswordlessConfirmation
            email={submittedEmail}
            onTryDifferentEmail={handleTryDifferentEmail}
          />
        ) : showOnlyMicrosoftLogin ? (
          <>
            <CardTitle align={"center"} className={styles.LoginTitle}>
              Welcome back
            </CardTitle>
            <p className={styles.LoginContent}>
              Use your Ipsos credentials to log in
            </p>
            <Button
              className={styles.LoginButton}
              onClick={microsoftLoginHandler}
              disabled={loading}
            >
              <MicrosoftLogo size={16} className={styles.MicrosoftLogo} /> Sign
              in with Microsoft {loading && <LoadingDots />}
            </Button>
          </>
        ) : showConditionalLogin ? (
          <ConditionalLoginForm
            onPasswordlessSubmit={passwordlessLoginHandler}
            onMicrosoftLogin={onSignInWithMicrosoft}
            disabled={loading}
            failedEmail={failedEmail}
          />
        ) : showOnlyPasswordless ? (
          <PasswordlessForm
            onSubmit={async (email: string) => {
              await passwordlessLoginHandler(email);
            }}
            disabled={loading}
            failedEmail={failedEmail}
          />
        ) : null}
      </Card>
    </div>
  );
};
